import { Component, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit, Inject, Optional } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { ClaimService } from 'src/app/_service/claim.service';
import { VesselService } from 'src/app/_service/vessel.service';
import { UserService } from 'src/app/_service/user.service';
import { CompanyService } from 'src/app/_service/company.service';
import { AttachmentService } from 'src/app/_service/attachment.service';
import { EquipmentService } from 'src/app/_service/equipment.service';
import { CommunicationService } from 'src/app/_service/communication.service';
import { TranslateService } from '@ngx-translate/core';
import { postEmailData } from 'src/app/_model/communication';
import { uploadedFile, postClaimForm } from 'src/app/_model/claim';
import { AssigneeUtil } from 'src/app/_common/common.assignee';
import { CLAIM_TAG_OPTIONS } from 'src/app/_model/string_literal';

import { environment } from "src/environments/environment";

import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { firstValueFrom, combineLatest, EMPTY, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-claim-new',
  templateUrl: './claim-new.component.html',
  styleUrl: './claim-new.component.css',
  animations: [
    trigger('fadeInOut', [
      state('visible', style({ opacity: 1 })),
      state('hidden', style({ opacity: 0 })),
      transition('visible => hidden', [animate('500ms ease-out')]),
      transition('hidden => visible', [animate('500ms ease-in')])
    ])
  ],
})
export class ClaimNewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('chipRef', { read: ElementRef}) chipRef!: ElementRef;
  readonly recordType: 'claim' | 'inquiry';

  get isInquiry(): boolean {
    return this.recordType === 'inquiry';
  }

  selectedVessel: any;
  selectedVesselEquip: any;
  userList: any[] = [];
  vesselList: any[] = [];
  vesselEquipList: any[] = [];
  form = new postClaimForm;
  claimForm = this.form.defaultForm;
  isVesselTouched = false;
  isEquipTouched = false;
  isSubmitted = false;
  isConfidential = 0;
  claimCompanyMembers: any[] = [];
  agentList: any = [];
  JRCS_ID = "09f08b32-1cbe-4b69-bf51-f3b8a742bc02";
  selectedAssignee: any = undefined;
  tags: string[] = [];
  updatedClaimEquipModel: any = undefined;
  updatedClaimEquip: any = undefined;
  updatedClaimProdModel: any = undefined;
  updatedClaimProd: any = undefined;
  updatedClaimProdCategory: any = undefined;

  vesselEquipRequest?: Subscription;
  csNumberSuggestRequest?: Subscription;

  csNumberDate = '';
  csNumberSequence = '';

  priority = [
    {
      "id": 1,
      "name": "high"
    },
    {
      "id": 2,
      "name": "medium"
    },
    {
      "id": 3,
      "name": "low"
    },
  ];

  mark = [
    {
      "id": 4,
      "name": "advice"
    },
    {
      "id": 0,
      "name": "guarantee"
    },
  ];
  selectedPriority = this.priority[1];
  selectedMark = this.mark[0];

  tagOptions = CLAIM_TAG_OPTIONS;
  tagQuery = "";
  showTagSuggestions = false;
  tagInputElement: HTMLInputElement | null = null;

  constructor(
    private claimService: ClaimService,
    private vesselService: VesselService,
    private userService: UserService,
    private equipmentService: EquipmentService,
    private attachmentService: AttachmentService,
    private communicationService: CommunicationService,
    private companyService: CompanyService,
    private translate: TranslateService,
    public dialogRef: MatDialogRef<ClaimNewComponent>,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) data: { recordType?: 'claim' | 'inquiry' } | null,
  ) {
    this.recordType = data?.recordType === 'inquiry' ? 'inquiry' : 'claim';
  }

  ngOnInit(): void {
    combineLatest([
      this.vesselService.vesselList$,
      this.userService.userList$,
    ]).subscribe(([vessels, users]) => {
      this.vesselList = vessels;
      this.userList = users;
      this.agentList = (users || [])
        .filter((u: any) => u.company_id === this.JRCS_ID)
        .map((u: any) => ({ ...u, displayLabel: AssigneeUtil.getLabel(u) }));
    });

    this.prefillCsNumber();
  }

  ngOnDestroy(): void {
    this.csNumberSuggestRequest?.unsubscribe();
  }

  private getJstDateKey(): string {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return `${values['year'].slice(-2)}${values['month']}${values['day']}`;
  }

  private prefillCsNumber(): void {
    const dateKey = this.getJstDateKey();
    this.csNumberDate = dateKey;

    this.csNumberSuggestRequest = this.claimService.suggestCsNumber(dateKey).subscribe({
      next: (res) => {
        if (this.csNumberDate === dateKey && !this.csNumberSequence) {
          this.csNumberSequence = String(res.sequence);
        }
      },
      error: () => { },
    });
  }

  ngAfterViewInit(): void {
    const inputEl = this.chipRef?.nativeElement?.querySelector('input');

    if (inputEl) {
      inputEl.addEventListener('paste', (event: ClipboardEvent) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        
        const pastedText = event.clipboardData?.getData('text') || '';

        const cursorPos = inputEl.selectionStart || 0;
        const currentValue = inputEl.value;
        const newValue =
          currentValue.slice(0, cursorPos) + pastedText + currentValue.slice(cursorPos);

        inputEl.value = newValue;
        inputEl.dispatchEvent(new Event('input', {bubbles: true}));
      }, true);
    } 
    
    const chipsHost = this.chipRef?.nativeElement;
    if (chipsHost) {
      chipsHost.addEventListener('click', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (
          (target.closest('.p-chips-token')) && !target.closest('.p-chips-token-icon')
        ) {
          event.preventDefault()
          event.stopImmediatePropagation();
        }
      }, true);
    }
  }

  unwrapFirst(response: any) {
    return response?.data?.[0] ?? null;
  }

  resetUpdatedEquipmentChain(): void {
    this.updatedClaimEquipModel = undefined;
    this.updatedClaimEquip = undefined;
    this.updatedClaimProdModel = undefined;
    this.updatedClaimProd = undefined;
    this.updatedClaimProdCategory = undefined;
  }

  onVesselEquipSelected(): void {
    this.vesselEquipRequest?.unsubscribe();
    this.resetUpdatedEquipmentChain();

    const equipmentModelId =
      this.selectedVesselEquip?.equipmentModelId;

    if (!equipmentModelId) {
      return;
    }

    this.vesselEquipRequest = this.equipmentService
      .getEquipModelById(equipmentModelId)
      .pipe(
        map(response => this.unwrapFirst(response)),

        tap(equipmentModel => {
          this.updatedClaimEquipModel = equipmentModel;
        }),

        switchMap(equipmentModel => {
          const equipmentId = equipmentModel?.equipmentId;

          return equipmentId
            ? this.equipmentService.getEquipById(equipmentId).pipe(
                map(response => this.unwrapFirst(response))
              )
            : of(null);
        }),

        tap(equipment => {
          this.updatedClaimEquip = equipment;
        }),

        switchMap(equipment => {
          const productModelId = equipment?.productModelId;

          return productModelId
            ? this.equipmentService.getProductModelById(productModelId).pipe(
                map(response => this.unwrapFirst(response))
              )
            : of(null);
        }),

        tap(productModel => {
          this.updatedClaimProdModel = productModel;
        }),

        switchMap(productModel => {
          const productId = productModel?.productId;

          return productId
            ? this.equipmentService.getProductById(productId).pipe(
                map(response => this.unwrapFirst(response))
              )
            : of(null);
        }),

        tap(product => {
          this.updatedClaimProd = product;
        }),

        switchMap(product => {
          const categoryId = product?.categoryId;

          return categoryId
            ? this.equipmentService.getCategoryById(categoryId).pipe(
                map(response => this.unwrapFirst(response))
              )
            : of(null);
        }),

        tap(category => {
          this.updatedClaimProdCategory = category;
        }),

        catchError(error => {
          console.error('Failed to load equipment chain', error);
          this.resetUpdatedEquipmentChain();
          return EMPTY;
        })
      )
      .subscribe();
  }

  getCompanyName(companyId: any) {
    return this.companyService.companyList.find(c => c.id === companyId)?.name || this.companyService.companyList.find(c => c.id === companyId)?.otherName || '';
  }

  onFileListChange(newData: any) {
    this.files = newData;
  }

  onConfidentialChange(event: any) {
    this.isConfidential = event.target.checked ? 1 : 0;
  }

  onVesselSelected() {
    if (this.selectedVessel) {
      this.equipmentService.getVesselEquipByVesselId(this.selectedVessel?.id).subscribe({
        next: (value: any) => {
          this.vesselEquipList = value?.data;
        },
        error: (err) => {
          console.error("Failed fetching vessel equipment list.")
        },
      });
    } else {
      this.claimCompanyMembers = [];
      this.selectedVesselEquip = undefined;
      this.vesselEquipList = [];
    }
  }

  files: uploadedFile[] = [];

  get vesselInvalid(): boolean {
    return (this.isVesselTouched || this.isSubmitted) && !this.selectedVessel;
  }

  get equipInvalid(): boolean {
    return (this.isEquipTouched || this.isSubmitted) && !this.selectedVesselEquip;
  }

  vesselTouched() {
    this.isVesselTouched = true;
  }

  equipTouched() {
    this.isEquipTouched = true;
  }

  getJSTTime() {
    const now = new Date();
  
    const jstDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
    );
  
    const pad = (n: any) => String(n).padStart(2, "0");
  
    const year = jstDate.getFullYear();
    const month = pad(jstDate.getMonth() + 1);
    const day = pad(jstDate.getDate());
    const hours = pad(jstDate.getHours());
    const minutes = pad(jstDate.getMinutes());
  
    return {
      usFormat: `${month}/${day}/${year} ${hours}:${minutes}`,
      jpFormat: `${year}年${month}月${day}日 ${hours}:${minutes}`,
    };
  }

  get filteredTagOptions() {
    const query = this.tagQuery.toLowerCase();

    const selectedTags = new Set(
      (this.tags || []).map(tag => tag.toLowerCase())
    );

    return this.tagOptions.filter(option => {
      const normalizedOption = option.toLowerCase();

      return (
        !selectedTags.has(normalizedOption) &&
        normalizedOption.includes(query)
      );
    });
  }

  onTagInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.tagName !== "INPUT") {
      return;
    }

    this.tagInputElement = input;
    this.tagQuery = input.value.trim();
    this.showTagSuggestions = true;

    this.scrollTagInputIntoView(input);
  }

  scrollTagInputIntoView(input: HTMLInputElement) {
    requestAnimationFrame(() => {
      const container = input.closest(
        ".p-chips-multiple-container"
      ) as HTMLElement | null;

      if (container) {
        container.scrollLeft = container.scrollWidth;
      }
    });
  }

  selectTagSuggestion(option: string, event: MouseEvent) {
    event.preventDefault();

    const alreadySelected = (this.tags || []).some(
      tag => tag.toLowerCase() === option.toLowerCase()
    );

    if (!alreadySelected) {
      this.tags = [...(this.tags || []), option];
    }

    this.clearTagInput();
  }

  clearTagInput() {
    this.tagQuery = "";
    this.showTagSuggestions = false;

    if (!this.tagInputElement) {
      return;
    }

    this.tagInputElement.value = "";
    this.tagInputElement.dispatchEvent(
      new Event("input", { bubbles: true })
    );
    this.tagInputElement.focus();
  }

  openTagSuggestions(event: Event) {
    const input = event.target as HTMLInputElement;

    this.tagInputElement = input;
    this.tagQuery = input.value.trim();
    this.showTagSuggestions = true;

    this.scrollTagInputIntoView(input);
  }

  closeTagSuggestions() {
    this.tagQuery = "";
    this.showTagSuggestions = false;
  }

  onTagAdded() {
    this.tagQuery = "";
    this.showTagSuggestions = true;

    if (this.tagInputElement) {
      this.scrollTagInputIntoView(this.tagInputElement);
    }
  }

  get csNumberInvalid(): boolean {
    const d = (this.csNumberDate ?? '').trim();
    const s = (this.csNumberSequence ?? '').trim();
    return !! (d || s) && (!/^\d{6}$/.test(d) || !/^[1-9]\d*$/.test(s))
  }

  async onSubmit() {
    this.isSubmitted = true;
    
    if (this.csNumberInvalid) {
      window.alert(
        '日付6桁（YYMMDD）と先頭に0を付けない正の整数を入力してください。 / Enter a six-digit date (YYMMDD) and a positive integer without leading zeros.'
      );
      return;
    }

    const attachmentArray = this.files.map(f => f.id || "").filter(id => id?.trim());
    const vesselManagerId = this.selectedVessel?.manager_id;

    for (const file of this.files) {
      try {
        await firstValueFrom(
          this.attachmentService.updateFile({ id: file.id, companyId: vesselManagerId })
        );
      } catch (err) {
        window.alert(
          this.translate.instant("updateFileErr", {
            fileName: file.name
          })
        );
        return;
      }
    }

    this.claimForm.get('attachmentIds')?.setValue(attachmentArray);

    this.claimForm.get('vesselId')?.setValue(this.selectedVessel?.id);
    this.claimForm.get('equipmentId')?.setValue(this.selectedVesselEquip?.id);
    this.claimForm.get('isConfidential')?.setValue(this.isConfidential);
    this.claimForm.get('assignee')?.setValue(this.selectedAssignee?.id);
    this.claimForm.get('priority')?.setValue(this.selectedPriority?.id);
    this.claimForm.get('mark')?.setValue(this.selectedMark?.id);
    this.claimForm.get('tags')?.setValue([...this.tags]);

    if (this.claimForm.invalid) {
      window.alert(this.translate.instant("formInvalid"));
      return;
    }

    const payload = { ...this.claimForm.value, recordType: this.recordType };

    for (const key of ['expectedPortCall', 'incidentDate'] as const) {
      const jstLocal = payload[key]?.trim();
      payload[key] = jstLocal ? new Date(`${jstLocal}+09:00`).toISOString() : payload[key];
    }

    delete payload.csNumberSuffix;

    const csDate = (this.csNumberDate ?? '').trim();
    const csSequence = (this.csNumberSequence ?? '').trim();

    if (csDate && csSequence) {
      payload.csNumberSuffix = `${csDate}-${csSequence}`;
    }

    this.claimService.submitNewClaim(payload).subscribe({
      error: (err) => {
        const message =
          (err.status === 400 || err.status === 409) &&
          typeof err.error?.message === 'string'
            ? err.error.message
            : this.translate.instant("err");

        window.alert(message);
      },
      next: (c: any) => {
        if (this.selectedVessel) {
          this.claimCompanyMembers = this.userList.filter(u => u.company_id === this.selectedVessel?.manager_id);
          const claimUpdateNotificationSubject = `【新規作成】お問合せ『${this.claimForm.value.title}』受付のお知らせ`;
          const claimUpdateNotificationContent = `
            JRCSへのお問合せ『${this.claimForm.value.title}』を ${this.getJSTTime().jpFormat}（JST）に受け付けました。<br>
            Inquiry to JRCS titled ${this.claimForm.value.title} was created at ${this.getJSTTime().usFormat} JST.<br><br>
            下記URLよりご確認ください。<br>
            Please refer to the following URL.<br><br>
            ${environment?.assistURL}/dashboard/support/${c?.id}
          `;
          const claimCreateEmail: postEmailData = {
            recipient_ids: this.claimCompanyMembers?.map(m => m?.id),
            subject: claimUpdateNotificationSubject,
            body: claimUpdateNotificationContent,
            attachment_ids: undefined,
          };

          // remove new claim email for now
          // this.communicationService.createNewEmail(claimCreateEmail).subscribe({
          //   error: (err) => { window.alert(this.translate.instant("sendEmailErr")); },
          // });
        }
      },
      complete: () => {
        window.alert(this.translate.instant("created"));
        this.dialogRef.close(true);
      },
    });
  }

  tagCopiedText = '';

  onChipMouseUp(event: MouseEvent) {
    const selection = window.getSelection();
    const text = selection ? selection.toString() : '';

    if (text) {
      this.tagCopiedText = text;
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }
}
